import { Icon } from '@foam/buoy/src/components/Icon/Icon';
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SOCIAL_PLATFORMS } from '~/constants';
import { Carousel } from '~/screens/PageBuilder/blocks/BrandExperience/Carousel';
import { MKTalentContentFields } from '~/screens/PageBuilder/blocks/TalentContent/TalentContent';
import { ContentImage } from '~/screens/SearchContent/components/ContentImage';

interface SelectedContentThumbnailsProps {
  contents: MKTalentContentFields[];
  onRemove: (content: MKTalentContentFields) => void;
}

interface PreviewPosition {
  readonly top: number;
  readonly left: number;
}

const PREVIEW_GUTTER = 8;
const PREVIEW_WIDTH = 200;
const VIEWPORT_MARGIN = 8;

const getPreviewPosition = (triggerRect: DOMRect): PreviewPosition => {
  const centerX = triggerRect.left + triggerRect.width / 2;
  const halfWidth = PREVIEW_WIDTH / 2;
  const minLeft = VIEWPORT_MARGIN + halfWidth;
  const maxLeft = window.innerWidth - VIEWPORT_MARGIN - halfWidth;

  return {
    top: triggerRect.bottom + PREVIEW_GUTTER,
    left: Math.max(minLeft, Math.min(maxLeft, centerX)),
  };
};

export const SelectedContentThumbnails = ({
  contents,
  onRemove,
}: SelectedContentThumbnailsProps) => {
  const [hoveredContentId, setHoveredContentId] = useState<string | null>(null);
  const triggerRefs = useRef<Record<string, HTMLDivElement | null>>({});

  if (contents.length === 0) {
    return null;
  }

  return (
    <Carousel
      containerClassName="overflow-x-auto"
      className="w-full"
      align="left"
    >
      {contents.map((content) => {
        const imageSrc =
          content.platform === SOCIAL_PLATFORMS.youtube
            ? content.assets?.[0]?.url || ''
            : content.assets?.[0]?.thumbnail || '';
        const isHovered = hoveredContentId === content.id;
        const triggerEl = triggerRefs.current[content.id];
        const triggerRect = isHovered
          ? triggerEl?.getBoundingClientRect()
          : undefined;
        const previewPos = triggerRect
          ? getPreviewPosition(triggerRect)
          : undefined;

        return (
          <div
            key={content.id}
            ref={(el) => {
              triggerRefs.current[content.id] = el;
            }}
            className="group/thumbnail relative h-20 shrink-0 cursor-pointer overflow-hidden rounded-4"
            onMouseEnter={() => setHoveredContentId(content.id)}
            onMouseLeave={() => setHoveredContentId(null)}
            onClick={() => onRemove(content)}
          >
            <ContentImage
              src={imageSrc}
              alt=""
              width="auto"
              height="100%"
              border="1px solid"
              borderColor="primary.100"
              borderRadius="4px"
              objectFit="cover"
            />
            <div className="absolute inset-0 flex items-center justify-center rounded-4 bg-black-alpha-50 opacity-0 transition-opacity group-hover/thumbnail:opacity-100">
              <Icon name="Trash" color="white" />
            </div>

            {isHovered &&
              previewPos &&
              createPortal(
                <div
                  className="pointer-events-none fixed z-50"
                  style={{
                    top: previewPos.top,
                    left: previewPos.left,
                    transform: 'translate(-50%, 0)',
                  }}
                >
                  <div
                    className="rounded-8 bg-[#fff] p-2 shadow-lg transition-all duration-200 ease-out"
                    style={{
                      width: 200,
                      opacity: isHovered ? 1 : 0,
                      transform: isHovered ? 'scale(1)' : 'scale(0.95)',
                    }}
                    onMouseEnter={() => setHoveredContentId(content.id)}
                    onMouseLeave={() => setHoveredContentId(null)}
                  >
                    <ContentImage
                      src={imageSrc}
                      alt=""
                      width="100%"
                      height="auto"
                      border="1px solid"
                      borderColor="primary.100"
                      borderRadius="4px"
                      objectFit="cover"
                    />
                  </div>
                </div>,
                document.body,
              )}
          </div>
        );
      })}
    </Carousel>
  );
};
